import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, Button, BloodTypeBadge, ComponentBadge, COMPONENT_LABELS, UrgencyBadge, Reveal, Skeleton, Select, useIsMobile } from '../components/ui';
import PageLayout from '../components/layout/PageLayout';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';

const URGENCY_BORDER = { critical: 'var(--red)', high: 'var(--amber)', normal: 'var(--green)' };

export default function Requests() {
  const { user } = useAuth();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [filters, setFilters] = useState({ blood_type: '', component: '', urgency: '' });
  const [hoveredId, setHoveredId] = useState(null);
  const isMobile = useIsMobile();

  useEffect(() => {
    setLoadError(false);
    api.get('/requests/', { params: { status: 'open', ...filters } })
      .then(r => setRequests(r.data))
      .catch(() => { setRequests([]); setLoadError(true); })
      .finally(() => setLoading(false));
  }, [filters]);

  const setF = k => e => setFilters(f => ({ ...f, [k]: e.target.value }));

  return (
    <PageLayout>
      <div style={{ marginBottom: '4px' }}>
        <h1 style={{ fontSize: '24px', fontWeight: 800, letterSpacing: '-0.03em', color: 'var(--gray-900)' }}>Blood Requests</h1>
        <p style={{ fontSize: '13.5px', color: 'var(--gray-500)', marginTop: '4px' }}>Respond to people who need help right now.</p>
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: '8px', margin: '16px 0', flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <Select style={{ width: isMobile ? '100%' : '148px', marginBottom: 0 }} value={filters.blood_type} onChange={setF('blood_type')}>
          <option value="">All Blood Types</option>
          {['A+','A-','B+','B-','AB+','AB-','O+','O-'].map(t => <option key={t}>{t}</option>)}
        </Select>
        <Select style={{ width: isMobile ? '100%' : '148px', marginBottom: 0 }} value={filters.component} onChange={setF('component')}>
          <option value="">All Types</option>
          {Object.entries(COMPONENT_LABELS).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
        </Select>
        <Select style={{ width: isMobile ? '100%' : '148px', marginBottom: 0 }} value={filters.urgency} onChange={setF('urgency')}>
          <option value="">All Urgency</option>
          <option value="critical">Critical</option>
          <option value="high">High</option>
          <option value="normal">Normal</option>
        </Select>
        <Link to="/requests/new" style={{ marginLeft: isMobile ? '0' : 'auto', width: isMobile ? '100%' : undefined }}>
          <Button variant="primary" style={{ width: isMobile ? '100%' : undefined }}>+ New Request</Button>
        </Link>
      </div>

      <Card>
        {loading ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            {[0, 1, 2, 3].map(i => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '13px 10px' }}>
                <Skeleton width="44px" height="28px" radius="8px" />
                <div style={{ flex: 1 }}>
                  <Skeleton width="50%" height="13px" style={{ marginBottom: '6px' }} />
                  <Skeleton width="30%" height="11px" />
                </div>
                <Skeleton width="60px" height="22px" radius="20px" />
                <Skeleton width="56px" height="28px" radius="8px" />
              </div>
            ))}
          </div>
        ) : loadError ? (
          <div style={{ textAlign: 'center', padding: '56px 0', color: 'var(--gray-400)' }}>
            <div style={{ fontSize: '36px', marginBottom: '10px', opacity: 0.6 }}>⚠️</div>
            <p style={{ fontSize: '14px', fontWeight: 500, color: 'var(--gray-600)' }}>Couldn't load requests right now.</p>
            <p style={{ fontSize: '13px', color: 'var(--gray-400)' }}>Please check your connection and try again.</p>
          </div>
        ) : requests.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '56px 0', color: 'var(--gray-400)' }}>
            <div style={{ fontSize: '36px', marginBottom: '10px', opacity: 0.6 }}>🩸</div>
            <p style={{ fontSize: '14px', fontWeight: 500, marginBottom: '4px', color: 'var(--gray-600)' }}>No open requests</p>
            <p style={{ fontSize: '13px', color: 'var(--gray-400)', marginBottom: '14px' }}>Be the first to post a blood request.</p>
            <Link to="/requests/new">
              <Button variant="primary" size="sm">Post a Request</Button>
            </Link>
          </div>
        ) : requests.map((r, i) => (
          <Reveal key={r.id} delay={Math.min(i, 6) * 40} as="div">
            <div
              onMouseEnter={() => setHoveredId(r.id)}
              onMouseLeave={() => setHoveredId(null)}
              style={{
                display: 'flex', alignItems: 'center', gap: '12px',
                padding: '13px 10px', borderRadius: '8px',
                borderBottom: i < requests.length - 1 ? '1px solid var(--gray-100)' : 'none',
                background: hoveredId === r.id ? 'var(--gray-50)' : 'transparent',
                borderLeft: `3px solid ${hoveredId === r.id ? URGENCY_BORDER[r.urgency] : 'transparent'}`,
                transition: 'background 0.15s, border-color 0.15s',
                flexWrap: isMobile ? 'wrap' : 'nowrap',
              }}
            >
              <BloodTypeBadge type={r.blood_type} />
              <div style={{ flex: 1, minWidth: '120px' }}>
                <div style={{ fontSize: '13.5px', fontWeight: 600, color: 'var(--gray-900)', display: 'flex', alignItems: 'center', gap: '6px' }}>
                  {r.hospital_name || (user ? '—' : 'Login to view hospital')}
                  <ComponentBadge component={r.component} />
                </div>
                <div style={{ fontSize: '12px', color: 'var(--gray-500)', marginTop: '2px' }}>
                  {[r.city, r.country].filter(Boolean).join(', ')} · {r.units_needed} unit{r.units_needed > 1 ? 's' : ''} · {new Date(r.created_at).toLocaleDateString()}
                </div>
              </div>
              <UrgencyBadge urgency={r.urgency} />
              <Link to={`/requests/${r.id}`}><Button size="sm" variant="outline">View</Button></Link>
            </div>
          </Reveal>
        ))}
      </Card>
    </PageLayout>
  );
}
